import { useEffect, useState } from "react";
import { ChevronDown, X } from "lucide-react";
import { cn } from "./ui";
import { t } from "@/lib/i18n";
import { workflowTriggers, type WorkflowTemplate } from "@/mocks/workflows";

/* Окно «Новый рабочий процесс»: имя, триггер и (если из шаблона) сводка шагов.
   Процесс создаётся черновиком — шаги настраиваются дальше. */

const FIELD =
  "w-full box-border rounded-btn border border-border bg-background px-3 text-[12.5px] text-foreground placeholder:text-muted-foreground/70 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25";

type Props = {
  template?: WorkflowTemplate | null;
  onClose: () => void;
  onCreate: (name: string, triggerId: string, template?: WorkflowTemplate | null) => void;
};

export default function WorkflowPanel({ template, onClose, onCreate }: Props) {
  const [name, setName] = useState(template ? template.name : "");
  const [trigger, setTrigger] = useState("conversation");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const ready = name.trim().length >= 2;

  const create = () => {
    setTouched(true);
    if (!ready) return;
    onCreate(name.trim(), trigger, template ?? null);
  };

  return (
    <div className="absolute inset-0 z-[70] flex items-center justify-center p-4">
      <button type="button" onClick={onClose} aria-label={t("Закрыть окно")} className="absolute inset-0 cursor-pointer bg-black/45 backdrop-blur-[1px]" />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="workflow-panel-title"
        className="relative flex w-full max-w-[26rem] flex-col overflow-hidden rounded-card border border-border bg-card shadow-lift"
      >
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <h4 id="workflow-panel-title" className="min-w-0 flex-1 truncate text-[13.5px] font-semibold">
            {template ? t("Использовать шаблон") : t("Новый рабочий процесс")}
          </h4>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("Закрыть окно")}
            className="grid size-7 flex-none cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>

        <div className="px-4 py-3.5">
          {template ? (
            <div className="mb-3 flex items-start gap-2 rounded-btn border border-border bg-background/60 p-2.5">
              <i className="ri-file-list-3-line mt-0.5 text-[13px] leading-none text-primary" aria-hidden />
              <p className="min-w-0 text-[10.5px] leading-relaxed text-muted-foreground">
                {t("Шаблон «{name}» — {n} шага действий. Все шаги можно поменять после создания.", {
                  name: template.name,
                  n: template.steps.filter((s) => s.kind !== "trigger").length,
                })}
              </p>
            </div>
          ) : null}

          <label className="block">
            <span className="text-[11.5px] font-semibold">{t("Название процесса")}</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              autoFocus
              placeholder={t("Например, Приветствие новых лидов")}
              aria-invalid={touched && !ready}
              className={cn(FIELD, "mt-1 h-9", touched && !ready && "border-red-500/60 focus:border-red-500 focus:ring-red-500/20")}
            />
            {touched && !ready ? <span className="mt-1 block text-[10.5px] text-red-600">{t("Введите название — от 2 символов")}</span> : null}
          </label>

          <label className="mt-3.5 block">
            <span className="text-[11.5px] font-semibold">{t("Триггер запуска")}</span>
            <span className="relative mt-1 block">
              <select
                value={trigger}
                onChange={(e) => setTrigger(e.target.value)}
                className={cn(FIELD, "h-9 appearance-none pe-8")}
              >
                {workflowTriggers.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute end-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
            </span>
            <span className="mt-1 block text-[10px] leading-relaxed text-muted-foreground">
              {t("Триггер — событие, после которого процесс начинает выполняться. Поменять его можно в редакторе.")}
            </span>
          </label>
        </div>

        <div className="flex items-center gap-2 border-t border-border px-4 py-2.5">
          {template ? null : (
            <button
              type="button"
              onClick={() => onCreate(name.trim() || t("Новый процесс"), trigger, null)}
              className="cursor-pointer whitespace-nowrap text-[11.5px] font-semibold text-primary underline decoration-dotted underline-offset-2 transition hover:brightness-110"
            >
              {t("Создать без шаблона")}
            </button>
          )}
          <div className="ms-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn px-3 text-[12.5px] font-semibold text-muted-foreground transition hover:text-foreground"
            >
              {t("Отмена")}
            </button>
            <button
              type="button"
              onClick={create}
              aria-disabled={!ready}
              className={cn(
                "inline-flex h-8 items-center justify-center whitespace-nowrap rounded-btn px-4 text-[12.5px] font-semibold transition",
                ready ? "cursor-pointer bg-primary text-primary-foreground hover:brightness-110" : "cursor-not-allowed bg-foreground/10 text-muted-foreground",
              )}
            >
              {t("Создать")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}